/**
 * NexaStay theme: preset themes + legacy build from theme_color.
 * Solid colors only – no linear-gradients. All values load dynamically from theme.
 */

const DEFAULT_ACCENT = '#4ce1b6';

/** Preset theme keys (gradient removed – use light/dark) */
export const THEME_PRESET_KEYS = ['dark', 'light', 'midnight'];

/**
 * Preset themes: solid backgrounds, clear text contrast. Accent override applied in getPresetTheme.
 */
export const NEXASTAY_PRESET_THEMES = {
  dark: {
    mode: 'dark',
    accent: '#4ce1b6',
    bg: '#12151c',
    paper: '#1e1e26',
    textPrimary: '#ffffff',
    textSecondary: 'rgba(255,255,255,0.88)',
    textMuted: 'rgba(255,255,255,0.6)',
    border: 'rgba(255,255,255,0.12)',
    borderAccent: 'rgba(76,225,182,0.4)',
    cardBg: '#252530',
    headerFooterBg: '#1a1a24',
  },
  light: {
    mode: 'light',
    accent: '#0d9488',
    bg: '#f5f7fa',
    paper: '#ffffff',
    textPrimary: '#1a1a2e',
    textSecondary: 'rgba(26,26,46,0.78)',
    textMuted: 'rgba(26,26,46,0.55)',
    border: 'rgba(0,0,0,0.1)',
    borderAccent: 'rgba(13,148,136,0.35)',
    cardBg: '#ffffff',
    headerFooterBg: '#ffffff',
  },
  midnight: {
    mode: 'dark',
    accent: '#38bdf8',
    bg: '#0f172a',
    paper: '#1e293b',
    textPrimary: '#f1f5f9',
    textSecondary: '#cbd5e1',
    textMuted: '#94a3b8',
    border: 'rgba(56,189,248,0.15)',
    borderAccent: 'rgba(56,189,248,0.4)',
    cardBg: '#1e293b',
    headerFooterBg: '#0f172a',
  },
};

/**
 * Get theme by preset key. Optionally override accent from hotel theme_color.
 */
export function getPresetTheme(presetKey, accentOverride) {
  const key = THEME_PRESET_KEYS.includes(presetKey) ? presetKey : 'dark';
  const theme = { ...NEXASTAY_PRESET_THEMES[key] };
  if (accentOverride && normalizeHex(accentOverride)) {
    theme.accent = normalizeHex(accentOverride);
    theme.borderAccent = `${theme.accent}40`;
  }
  return theme;
}

/**
 * Parse hex to RGB (0-255). Supports #fff, #ffffff.
 */
function hexToRgb(hex) {
  const h = (hex || '').trim().replace(/^#/, '');
  if (h.length === 3) {
    const r = parseInt(h[0] + h[0], 16);
    const g = parseInt(h[1] + h[1], 16);
    const b = parseInt(h[2] + h[2], 16);
    return { r, g, b };
  }
  if (h.length === 6) {
    return {
      r: parseInt(h.slice(0, 2), 16),
      g: parseInt(h.slice(2, 4), 16),
      b: parseInt(h.slice(4, 6), 16),
    };
  }
  return { r: 76, g: 225, b: 182 };
}

/**
 * Relative luminance (0–1). Simple sRGB formula.
 */
function getLuminance(hex) {
  const { r, g, b } = hexToRgb(hex);
  const [rs, gs, bs] = [r, g, b].map((c) => {
    const s = c / 255;
    return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Build full theme from theme_color (hex). Legacy: used when no preset.
 */
export function buildNexaStayTheme(themeColor) {
  const accent = normalizeHex(themeColor) || DEFAULT_ACCENT;
  const luminance = getLuminance(accent);
  const isLightMode = luminance > 0.5;

  if (isLightMode) {
    return {
      mode: 'light',
      accent,
      bg: '#f5f7fa',
      paper: '#ffffff',
      textPrimary: '#1a1a2e',
      textSecondary: 'rgba(26,26,46,0.78)',
      textMuted: 'rgba(26,26,46,0.55)',
      border: 'rgba(0,0,0,0.12)',
      borderAccent: `${accent}40`,
      cardBg: '#ffffff',
      headerFooterBg: '#ffffff',
    };
  }

  return {
    mode: 'dark',
    accent,
    bg: '#12151c',
    paper: '#1e1e26',
    textPrimary: '#ffffff',
    textSecondary: 'rgba(255,255,255,0.85)',
    textMuted: 'rgba(255,255,255,0.6)',
    border: 'rgba(255,255,255,0.12)',
    borderAccent: `${accent}40`,
    cardBg: '#252530',
    headerFooterBg: '#1a1a24',
  };
}

export function normalizeHex(hex) {
  if (!hex || typeof hex !== 'string') return null;
  const h = hex.trim();
  return h.startsWith('#') ? h : '#' + h;
}

export { DEFAULT_ACCENT };
